import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, X, Edit2, Sparkles, Image as ImgIcon, Video, Filter, RefreshCw, Maximize2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageContainer, PageHeader, EmptyState } from "@/components/Page";

const PLACEMENT_LABELS = {
  "facebook-feed": "FB Feed", "facebook-story": "FB Story",
  "instagram-feed": "IG Feed", "instagram-story": "IG Story",
};
const PLATFORM_COLORS = {
  facebook: "bg-blue-50 text-blue-700 border-blue-200",
  instagram: "bg-pink-50 text-pink-700 border-pink-200",
};

const DraftThumbnail = ({ draft, loadMedia, mediaCache, onOpen, testId }) => {
  const [media, setMedia] = React.useState(mediaCache[draft.id] || null);
  const hasAny = draft.has_image || draft.has_video;

  useEffect(() => {
    let cancelled = false;
    if (hasAny && !media) {
      loadMedia(draft).then((m) => { if (!cancelled && m) setMedia(m); });
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.id]);

  return (
    <div
      className="aspect-square bg-gray-100 rounded mb-2 flex items-center justify-center overflow-hidden relative group cursor-pointer"
      onClick={() => hasAny && onOpen()}
      data-testid={testId}
    >
      {media?.video_url ? (
        <video src={media.video_url} className="w-full h-full object-cover" controls={false} muted />
      ) : media?.image_url ? (
        <img src={media.image_url} alt="" className="w-full h-full object-cover" />
      ) : hasAny ? (
        <div className="text-gray-300 flex flex-col items-center gap-1">
          <div className="w-6 h-6 border-2 border-gray-300 border-t-[#7C3AED] rounded-full animate-spin" />
          <span className="text-[10px]">loading…</span>
        </div>
      ) : (
        <div className="text-gray-300 flex flex-col items-center gap-1">
          {draft.placement === "story" ? <Video size={28} /> : <ImgIcon size={28} />}
          <span className="text-[10px]">no media</span>
        </div>
      )}
      {hasAny && (
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
          <Maximize2 size={20} className="text-white" />
        </div>
      )}
    </div>
  );
};

const ApprovalQueue = () => {
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState("");
  const [drafts, setDrafts] = useState([]);
  const [kpis, setKpis] = useState({});
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("pending");
  const [selected, setSelected] = useState(new Set());
  const [generating, setGenerating] = useState(false);
  const [genJob, setGenJob] = useState(null); // {current,total,message,status}
  const genPollRef = useRef(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ caption: "", hashtags: "" });
  const [preview, setPreview] = useState(null); // { draft } when open
  const [mediaCache, setMediaCache] = useState({}); // draft_id -> { image_url, video_url }

  const loadMedia = async (draft) => {
    if (mediaCache[draft.id]) return mediaCache[draft.id];
    try {
      const { data } = await api.get(`/content/drafts/${draft.id}/media`);
      setMediaCache((p) => ({ ...p, [draft.id]: data }));
      return data;
    } catch (e) {
      return null;
    }
  };

  const openPreview = async (draft) => {
    setPreview({ draft, loading: true });
    const media = await loadMedia(draft);
    setPreview({ draft: { ...draft, ...(media || {}) }, loading: false });
  };

  useEffect(() => {
    api.get("/brands").then(({ data }) => {
      const list = data.brands || [];
      setBrands(list);
      if (list[0]) setBrandId(list[0].id);
    }).catch(() => toast.error("Failed to load brands"));
  }, []);

  const load = async () => {
    if (!brandId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/content/approval-queue", { params: { brand_id: brandId, status: filter } });
      setDrafts(data.drafts || []);
      setKpis(data.kpis || {});
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || "Unknown error";
      toast.error(`Failed to load queue (${status || "no response"}): ${detail}`);
      console.error("Approval queue load error:", e?.response || e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [brandId, filter]);

  // Group drafts by concept (group_id) so 4 placements show side-by-side
  const grouped = useMemo(() => {
    const map = {};
    drafts.forEach((d) => {
      (map[d.group_id] = map[d.group_id] || []).push(d);
    });
    return Object.values(map);
  }, [drafts]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectGroup = (group) => {
    const all = group.every((d) => selected.has(d.id));
    setSelected((prev) => {
      const next = new Set(prev);
      group.forEach((d) => { if (all) next.delete(d.id); else next.add(d.id); });
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === drafts.length) setSelected(new Set());
    else setSelected(new Set(drafts.map((d) => d.id)));
  };

  const [scheduleModal, setScheduleModal] = useState(null); // { count } when open
  const [scheduleMode, setScheduleMode] = useState("now");
  const [scheduleInterval, setScheduleInterval] = useState(60);
  const [scheduleStartAt, setScheduleStartAt] = useState(() => {
    // default to "tomorrow at 9 AM local"
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    return d.toISOString().slice(0, 16);
  });
  const [approving, setApproving] = useState(false);

  const openApproveModal = () => {
    if (selected.size === 0) { toast.error("Select drafts to approve"); return; }
    // Count distinct concepts (group_id) in the selection
    const groups = new Set();
    drafts.filter((d) => selected.has(d.id)).forEach((d) => groups.add(d.group_id));
    setScheduleModal({ draftCount: selected.size, conceptCount: groups.size });
  };

  const submitApprove = async () => {
    if (!scheduleModal) return;
    setApproving(true);
    try {
      const body = {
        draft_ids: Array.from(selected),
        mode: scheduleMode,
        interval_minutes: scheduleInterval,
      };
      if (scheduleMode === "custom") {
        body.start_at = new Date(scheduleStartAt).toISOString();
      }
      const { data } = await api.post("/content/approve", body);
      toast.success(`Approved ${data.approved} drafts — ${scheduleMode === "now" ? "publishing starts in 1 minute" : "scheduled for posting"}`);
      setSelected(new Set());
      setScheduleModal(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally {
      setApproving(false);
    }
  };

  const reject = async () => {
    if (selected.size === 0) return;
    if (!confirm(`Reject ${selected.size} drafts?`)) return;
    try {
      const { data } = await api.post("/content/reject", { draft_ids: Array.from(selected) });
      toast.success(`Rejected ${data.rejected}`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error("Reject failed");
    }
  };

  const deleteSelected = async () => {
    if (selected.size === 0) return;
    if (!confirm(`PERMANENTLY DELETE ${selected.size} draft${selected.size === 1 ? "" : "s"}? This cannot be undone.`)) return;
    try {
      const { data } = await api.post("/content/delete", { draft_ids: Array.from(selected) });
      toast.success(`Deleted ${data.deleted}`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const purgeStatus = async () => {
    if (!brandId) return;
    const statuses = filter === "rejected" ? ["rejected"]
      : filter === "failed" ? ["failed"]
      : ["rejected", "failed"];
    const label = statuses.join(" + ");
    if (!confirm(`PERMANENTLY DELETE all ${label} drafts for this brand? This cannot be undone.`)) return;
    try {
      const { data } = await api.post("/content/purge", { brand_id: brandId, statuses });
      toast.success(`Deleted ${data.deleted} ${label} drafts`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purge failed");
    }
  };

  const pollJob = (jobId) => {
    if (genPollRef.current) clearInterval(genPollRef.current);
    genPollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/content/jobs/${jobId}`);
        setGenJob(data);
        if (data.status === "completed") {
          clearInterval(genPollRef.current);
          genPollRef.current = null;
          setGenerating(false);
          toast.success(data.message || "Batch ready");
          load();
          setTimeout(() => setGenJob(null), 2500);
        } else if (data.status === "failed") {
          clearInterval(genPollRef.current);
          genPollRef.current = null;
          setGenerating(false);
          toast.error(data.error || data.message || "Generation failed");
          setTimeout(() => setGenJob(null), 4000);
        }
      } catch (e) {
        // ignore transient errors
      }
    }, 2000);
  };

  useEffect(() => () => { if (genPollRef.current) clearInterval(genPollRef.current); }, []);

  // On brand change, check for any in-progress job and resume polling.
  // This keeps the progress bar visible after a page refresh.
  useEffect(() => {
    if (!brandId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/content/jobs/active/${brandId}`);
        if (data && !cancelled) {
          setGenerating(true);
          setGenJob(data);
          pollJob(data.id);
        }
      } catch (e) {
        // no active job
      }
    })();
    return () => { cancelled = true; if (genPollRef.current) clearInterval(genPollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId]);

  const generateNow = async (force = false) => {
    const msg = force
      ? "REGENERATE: this deletes today's pending drafts and creates a fresh batch. Already-approved/scheduled drafts stay. Continue?"
      : "Generate a fresh batch for this brand? Image generation takes 2-4 minutes (10 concepts × GPT Image 1). You can watch progress here.";
    if (!confirm(msg)) return;
    setGenerating(true);
    setGenJob({ status: "queued", current: 0, total: 11, message: "Starting…" });
    try {
      const { data } = await api.post(`/brands/${brandId}/content/generate${force ? "?force=true" : ""}`);
      if (data.job_id) {
        if (data.already_running) toast.info("A generation is already in progress — resuming progress view.");
        pollJob(data.job_id);
      } else {
        // Legacy fallback (e.g., already_exists from older backend)
        setGenerating(false);
        setGenJob(null);
        if (data.already_exists) toast.info("Today's batch already exists — use Regenerate.");
      }
    } catch (e) {
      setGenerating(false);
      setGenJob(null);
      toast.error(e?.response?.data?.detail || "Generation failed");
    }
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/content/drafts/${editingId}`, editForm);
      toast.success("Saved");
      setEditingId(null);
      load();
    } catch (e) {
      toast.error("Save failed");
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Approval Queue"
        subtitle="Review AI-generated posts before they go live. 4 placements per concept (FB feed + FB story + IG feed + IG story)."
        testId="approval-header"
        actions={
          <div className="flex gap-2">
            <button onClick={() => generateNow(false)} disabled={!brandId || generating} className="op-btn-secondary" data-testid="generate-batch-btn">
              <Sparkles size={16} /> {generating ? "Generating…" : "Generate Now"}
            </button>
            <button onClick={() => generateNow(true)} disabled={!brandId || generating} className="op-btn-secondary text-xs" data-testid="regenerate-batch-btn" title="Wipe today's pending drafts and generate a fresh batch">
              ↻ Regenerate
            </button>
            <button onClick={load} className="op-btn-secondary"><RefreshCw size={16} /></button>
          </div>
        }
      />

      {/* Progress bar for batch generation */}
      {genJob && (
        <div className="op-card p-4 mb-4 border-2 border-[#7C3AED]" data-testid="generation-progress">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Sparkles size={18} className={`text-[#7C3AED] ${genJob.status === "running" || genJob.status === "queued" ? "animate-pulse" : ""}`} />
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  {genJob.status === "queued" && "Queued…"}
                  {genJob.status === "running" && `Generating content (${genJob.current || 0}/${genJob.total || "?"})`}
                  {genJob.status === "completed" && "✓ Batch ready"}
                  {genJob.status === "failed" && "✗ Generation failed"}
                </p>
                <p className="text-xs text-gray-500" data-testid="progress-message">{genJob.message || "Working…"}</p>
              </div>
            </div>
            {genJob.status === "running" && genJob.total > 1 && (
              <p className="text-xs text-gray-500 tabular-nums">
                ~{Math.max(1, Math.round((genJob.total - (genJob.current || 0)) * 25))}s remaining
              </p>
            )}
          </div>
          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-500 ${
                genJob.status === "failed" ? "bg-rose-500"
                : genJob.status === "completed" ? "bg-emerald-500"
                : "bg-gradient-to-r from-[#7C3AED] to-[#EC4899]"
              } ${genJob.status === "running" ? "animate-pulse" : ""}`}
              style={{ width: `${genJob.total ? Math.min(100, ((genJob.current || 0) / genJob.total) * 100) : 5}%` }}
              data-testid="progress-bar"
            />
          </div>
        </div>
      )}

      <div className="op-card p-4 mb-4 flex flex-wrap items-center gap-3">
        <select className="op-input w-auto min-w-[200px]" value={brandId} onChange={(e) => { setBrandId(e.target.value); setSelected(new Set()); }} data-testid="aq-brand-select">
          {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          {["pending", "scheduled", "published", "rejected", "failed"].map((s) => (
            <button key={s} onClick={() => { setFilter(s); setSelected(new Set()); }}
                    className={`px-3 py-1 text-xs rounded ${filter === s ? "bg-white shadow text-[#7C3AED] font-semibold" : "text-gray-600"}`}
                    data-testid={`aq-filter-${s}`}>
              {s} {kpis[s] != null && <span className="ml-1 opacity-60">({kpis[s]})</span>}
            </button>
          ))}
        </div>
        {filter === "pending" && drafts.length > 0 && (
          <div className="ml-auto flex gap-2 flex-wrap">
            <button onClick={selectAll} className="op-btn-secondary text-xs">
              {selected.size === drafts.length ? "Deselect all" : `Select all (${drafts.length})`}
            </button>
            <button onClick={deleteSelected} disabled={selected.size === 0} className="op-btn-secondary text-rose-700 disabled:opacity-50" data-testid="aq-delete-btn" title="Permanently delete selected drafts">
              <Trash2 size={14} /> Delete ({selected.size})
            </button>
            <button onClick={reject} disabled={selected.size === 0} className="op-btn-secondary text-red-600 disabled:opacity-50" data-testid="aq-reject-btn">
              <X size={14} /> Reject ({selected.size})
            </button>
            <button onClick={openApproveModal} disabled={selected.size === 0} className="op-btn-primary disabled:opacity-50" data-testid="aq-approve-btn">
              <Check size={14} /> Approve ({selected.size})
            </button>
          </div>
        )}
        {(filter === "rejected" || filter === "failed") && drafts.length > 0 && (
          <div className="ml-auto flex gap-2 flex-wrap">
            {selected.size > 0 && (
              <button onClick={deleteSelected} className="op-btn-secondary text-rose-700" data-testid="aq-delete-selected-btn">
                <Trash2 size={14} /> Delete ({selected.size})
              </button>
            )}
            <button onClick={purgeStatus} className="op-btn-secondary text-rose-700 border-rose-200" data-testid="aq-purge-btn" title={`Permanently delete all ${filter} drafts`}>
              <Trash2 size={14} /> Clear all {filter} ({drafts.length})
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="op-card p-10 text-center text-gray-500">Loading…</div>
      ) : drafts.length === 0 ? (
        <div className="op-card">
          <EmptyState
            icon={Sparkles}
            title={filter === "pending" ? "No pending drafts" : `No ${filter} drafts`}
            description={filter === "pending"
              ? "Click 'Generate Now' to create a fresh batch of 10 concepts (40 placements) for this brand."
              : "Try a different filter or generate new content."}
            action={filter === "pending" && brandId
              ? <button onClick={() => generateNow(false)} disabled={generating} className="op-btn-primary"><Sparkles size={16} /> {generating ? "Generating…" : "Generate Today's Batch"}</button>
              : null}
          />
        </div>
      ) : (
        <div className="space-y-4">
          {grouped.map((group, idx) => {
            const allSelected = group.every((d) => selected.has(d.id));
            const topic = group[0]?.topic || `Concept ${idx + 1}`;
            return (
              <div key={group[0]?.group_id || idx} className={`op-card p-4 ${allSelected ? "ring-2 ring-[#7C3AED]" : ""}`} data-testid={`concept-${group[0]?.group_id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="font-semibold text-gray-900">{topic}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{new Date(group[0]?.created_at).toLocaleString()}</p>
                  </div>
                  {(filter === "pending" || filter === "rejected" || filter === "failed") && (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={allSelected} onChange={() => selectGroup(group)} />
                      <span className="text-xs text-gray-600">Select all 4 placements</span>
                    </label>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                  {group.map((d) => {
                    const k = `${d.platform}-${d.placement}`;
                    const isEditing = editingId === d.id;
                    return (
                      <div key={d.id} className={`border rounded-lg p-3 ${selected.has(d.id) ? "border-[#7C3AED] bg-purple-50/30" : "border-gray-200"}`} data-testid={`draft-${d.id}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-xs px-2 py-0.5 rounded border font-medium ${PLATFORM_COLORS[d.platform] || ""}`}>
                            {PLACEMENT_LABELS[k]}
                          </span>
                          {(filter === "pending" || filter === "rejected" || filter === "failed") && (
                            <input type="checkbox" checked={selected.has(d.id)} onChange={() => toggleSelect(d.id)} />
                          )}
                        </div>
                        <DraftThumbnail draft={d} loadMedia={loadMedia} mediaCache={mediaCache} onOpen={() => openPreview(d)} testId={`draft-media-${d.id}`} />
                        {isEditing ? (
                          <div className="space-y-2">
                            <textarea className="op-input text-xs" rows={3} value={editForm.caption} onChange={(e) => setEditForm({ ...editForm, caption: e.target.value })} />
                            <input className="op-input text-xs" placeholder="Hashtags" value={editForm.hashtags} onChange={(e) => setEditForm({ ...editForm, hashtags: e.target.value })} />
                            <div className="flex gap-1">
                              <button onClick={() => setEditingId(null)} className="op-btn-secondary text-xs flex-1">Cancel</button>
                              <button onClick={saveEdit} className="op-btn-primary text-xs flex-1">Save</button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <p className="text-xs text-gray-700 line-clamp-4 mb-1">{d.caption}</p>
                            {d.hashtags && <p className="text-[10px] text-blue-600 line-clamp-2">{d.hashtags}</p>}
                            {d.scheduled_at && filter === "scheduled" && (
                              <p className="text-[10px] text-amber-600 mt-1">⏰ {new Date(d.scheduled_at).toLocaleString()}</p>
                            )}
                            {d.error && (
                              <p className="text-[10px] text-red-600 mt-1 line-clamp-2">⚠ {d.error}</p>
                            )}
                            {filter === "pending" && (
                              <button onClick={() => { setEditingId(d.id); setEditForm({ caption: d.caption || "", hashtags: d.hashtags || "" }); }}
                                      className="text-[10px] text-gray-500 hover:text-[#7C3AED] mt-2 flex items-center gap-1">
                                <Edit2 size={10} /> Edit
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Media preview modal */}
      {preview && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setPreview(null)}
          data-testid="draft-preview-modal"
        >
          <div
            className="bg-white rounded-2xl max-w-5xl w-full max-h-[92vh] overflow-hidden flex flex-col md:flex-row shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="md:w-2/3 bg-black flex items-center justify-center min-h-[300px]">
              {preview.draft.video_url ? (
                <video src={preview.draft.video_url} className="w-full max-h-[80vh] object-contain" controls autoPlay />
              ) : preview.draft.image_url ? (
                <img src={preview.draft.image_url} alt="" className="w-full max-h-[80vh] object-contain" />
              ) : (
                <p className="text-gray-300">No media</p>
              )}
            </div>
            <div className="md:w-1/3 p-5 overflow-y-auto">
              <div className="flex items-center justify-between mb-3">
                <span className={`text-xs px-2 py-1 rounded border font-medium ${PLATFORM_COLORS[preview.draft.platform] || ""}`}>
                  {PLACEMENT_LABELS[`${preview.draft.platform}-${preview.draft.placement}`]}
                </span>
                <button onClick={() => setPreview(null)} className="text-gray-400 hover:text-gray-900" data-testid="close-preview">
                  <X size={18} />
                </button>
              </div>
              {preview.draft.topic && (
                <p className="text-sm font-semibold text-gray-900 mb-2">{preview.draft.topic}</p>
              )}
              <div className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-1">Caption</div>
              <p className="text-sm text-gray-800 whitespace-pre-wrap mb-4">{preview.draft.caption || <span className="text-gray-400 italic">no caption</span>}</p>
              {preview.draft.hashtags && (
                <>
                  <div className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-1">Hashtags</div>
                  <p className="text-sm text-blue-600 mb-4 break-words">{preview.draft.hashtags}</p>
                </>
              )}
              {preview.draft.overlay_text && (
                <>
                  <div className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-1">Story overlay</div>
                  <p className="text-sm text-gray-800 mb-4">"{preview.draft.overlay_text}"</p>
                </>
              )}
              <div className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-1">Status</div>
              <p className="text-sm text-gray-700 mb-4">{preview.draft.status}</p>
              {preview.draft.scheduled_at && (
                <>
                  <div className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-1">Scheduled</div>
                  <p className="text-sm text-amber-600 mb-4">{new Date(preview.draft.scheduled_at).toLocaleString()}</p>
                </>
              )}
              {preview.draft.error && (
                <div className="bg-red-50 border border-red-200 rounded p-2 text-xs text-red-700 mb-4">
                  ⚠ {preview.draft.error}
                </div>
              )}
              <p className="text-[10px] text-gray-400 mt-4">Created: {new Date(preview.draft.created_at).toLocaleString()}</p>
            </div>
          </div>
        </div>
      )}

      {/* Schedule / Approve modal */}
      {scheduleModal && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !approving && setScheduleModal(null)}
          data-testid="schedule-modal"
        >
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Schedule {scheduleModal.draftCount} drafts</h3>
              <button onClick={() => setScheduleModal(null)} className="text-gray-400 hover:text-gray-900" data-testid="schedule-close">
                <X size={18} />
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              These {scheduleModal.draftCount} drafts cover {scheduleModal.conceptCount} concept{scheduleModal.conceptCount === 1 ? "" : "s"}. Each concept publishes to FB feed + FB story + IG feed + IG story at the same time.
            </p>

            <label className="block text-sm font-semibold text-gray-800 mb-2">When should posting start?</label>
            <div className="space-y-2 mb-5">
              <label className="flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors" style={{borderColor: scheduleMode === "now" ? "#7C3AED" : "#E5E7EB", background: scheduleMode === "now" ? "#FAF5FF" : "#fff"}}>
                <input type="radio" checked={scheduleMode === "now"} onChange={() => setScheduleMode("now")} className="mt-1" data-testid="schedule-mode-now" />
                <div className="flex-1">
                  <p className="font-medium text-sm">Start now</p>
                  <p className="text-xs text-gray-500">First concept publishes in 1 minute, then spaced by the interval below.</p>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors" style={{borderColor: scheduleMode === "queue" ? "#7C3AED" : "#E5E7EB", background: scheduleMode === "queue" ? "#FAF5FF" : "#fff"}}>
                <input type="radio" checked={scheduleMode === "queue"} onChange={() => setScheduleMode("queue")} className="mt-1" data-testid="schedule-mode-queue" />
                <div className="flex-1">
                  <p className="font-medium text-sm">Append to brand's posting window</p>
                  <p className="text-xs text-gray-500">Slots into the brand's 8 AM–5 PM UTC window, one concept per hour, after any already-scheduled posts.</p>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors" style={{borderColor: scheduleMode === "custom" ? "#7C3AED" : "#E5E7EB", background: scheduleMode === "custom" ? "#FAF5FF" : "#fff"}}>
                <input type="radio" checked={scheduleMode === "custom"} onChange={() => setScheduleMode("custom")} className="mt-1" data-testid="schedule-mode-custom" />
                <div className="flex-1">
                  <p className="font-medium text-sm">Custom start time</p>
                  <p className="text-xs text-gray-500 mb-2">Pick exactly when the first concept goes live.</p>
                  {scheduleMode === "custom" && (
                    <input type="datetime-local" className="op-input text-sm w-full" value={scheduleStartAt} onChange={(e) => setScheduleStartAt(e.target.value)} data-testid="schedule-start-at" />
                  )}
                </div>
              </label>
            </div>

            <label className="block text-sm font-semibold text-gray-800 mb-2">Interval between concepts</label>
            <div className="flex items-center gap-2 mb-5">
              <select
                className="op-input w-auto text-sm"
                value={scheduleInterval}
                onChange={(e) => setScheduleInterval(Number(e.target.value))}
                data-testid="schedule-interval"
              >
                <option value={5}>Every 5 minutes</option>
                <option value={15}>Every 15 minutes</option>
                <option value={30}>Every 30 minutes</option>
                <option value={60}>Every 1 hour</option>
                <option value={120}>Every 2 hours</option>
                <option value={240}>Every 4 hours</option>
                <option value={720}>Every 12 hours</option>
                <option value={1440}>Once a day</option>
              </select>
              <p className="text-xs text-gray-500 flex-1">
                {scheduleModal.conceptCount} concepts · last post at ~{
                  (() => {
                    const start = scheduleMode === "custom" ? new Date(scheduleStartAt) : new Date(Date.now() + 60_000);
                    const last = new Date(start.getTime() + (scheduleModal.conceptCount - 1) * scheduleInterval * 60_000);
                    return last.toLocaleString();
                  })()
                }
              </p>
            </div>

            <div className="flex items-center justify-end gap-2 pt-4 border-t border-gray-100">
              <button onClick={() => setScheduleModal(null)} disabled={approving} className="op-btn-secondary text-sm" data-testid="schedule-cancel">Cancel</button>
              <button onClick={submitApprove} disabled={approving} className="op-btn-primary text-sm" data-testid="schedule-confirm">
                <Check size={14} /> {approving ? "Scheduling…" : `Approve ${scheduleModal.draftCount} & Schedule`}
              </button>
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  );
};

export default ApprovalQueue;
